import React from 'react';
import axios from 'axios';
import { isNil } from 'lodash';

import {
  ASYNC_EXCHANGE_STATUS,
  ASYNC_FETCH_RETRIES,
  ASYNC_POLL_INTERVAL_CONNECTED_MS,
  ASYNC_POLL_INTERVAL_MS,
  ASYNC_POLL_TIMEOUT_MS,
  type AsyncExchangeClientStatus,
  HTTP_STATUS,
} from '~/components/AiChat/constants/asyncChat';
import { reportAxiosError } from '~/Utils';

// The submit → poll → resolve state machine of an async chat exchange. The chat binds its own routes
// and payloads through the transport; to the runner an exchange id is an opaque string.

export interface ExchangeSubmitResult<TToken> {
  exchangeId: string;
  // A fresh token for the real-time subscription that carries the exchange's `done` event.
  realTimeToken?: TToken | null;
}

// A transport maps a status it does not know (or none) to `processing`, which the runner waits out.
export type ExchangeFetchResult<TExchange> =
  | { status: typeof ASYNC_EXCHANGE_STATUS.COMPLETED; exchange: TExchange }
  | { status: typeof ASYNC_EXCHANGE_STATUS.FAILED; error?: string | null }
  | { status: typeof ASYNC_EXCHANGE_STATUS.PROCESSING };

// A persisted exchange of the loaded chat. Reload recovery re-attaches to it by this id.
export interface ExchangeRow {
  id: string | number;
}

export interface ExchangeTransport<TInput, TExtra, TExchange, TRow extends ExchangeRow, TToken> {
  // `extra` shapes this one send (e.g. a regenerate). Retry re-sends the input without it.
  submit: (input: TInput, extra?: TExtra) => Promise<ExchangeSubmitResult<TToken>>;
  fetch: (exchangeId: string) => Promise<ExchangeFetchResult<TExchange>>;
  // Reload recovery: is this persisted row still running, and what input did it send?
  isInFlight: (row: TRow) => boolean;
  inputOf: (row: TRow) => TInput;
}

export interface ExchangeRunnerState<TInput> {
  exchangeId: string;
  input: TInput;
  status: AsyncExchangeClientStatus;
  error?: string | null;
}

export interface UseExchangeRunnerParams<TInput, TExtra, TExchange, TRow extends ExchangeRow, TToken> {
  // Memoize it on what its requests depend on (the route): its identity keys the runner's callbacks.
  transport: ExchangeTransport<TInput, TExtra, TExchange, TRow, TToken>;
  // The loaded chat's persisted exchanges, oldest first.
  rows: readonly TRow[];
  // Live real-time subscription connected? Drives the poll cadence (slow backstop vs fast fallback).
  isConnected: boolean;
  // Changes when the conversation changes; the run is torn down so it cannot leak into the next one.
  resetKey: string;
  // Called when an exchange resolves as completed — hand the answer back to the chat to render.
  onResolved: (exchange: TExchange) => void;
  onRealTimeToken: (token: TToken) => void;
}

export interface UseExchangeRunnerReturn<TInput, TExtra> {
  active: ExchangeRunnerState<TInput> | null;
  isFetching: boolean;
  submit: (input: TInput, extra?: TExtra) => Promise<void>;
  // Re-submits the failed exchange's input as a fresh exchange (mirrors submit — retry must not skip
  // the `done` fast path).
  retry: () => Promise<void>;
  // The `done` fast path: fetch now, retrying a transient failure.
  refresh: (exchangeId: string) => Promise<void>;
}

// No response (network error, timeout), rate-limited, or a server error.
const isTransientStatus = (status: number | undefined): boolean =>
  isNil(status) || status === HTTP_STATUS.TOO_MANY_REQUESTS || status >= HTTP_STATUS.INTERNAL_SERVER_ERROR;

// Statuses that mean the exchange is still running. The loader, the `done` fast path and the fallback
// poll all key off this one predicate, so the set lives in a single place.
const IN_FLIGHT_STATUSES: readonly AsyncExchangeClientStatus[] = [ASYNC_EXCHANGE_STATUS.PROCESSING];

export const isExchangeInFlight = <TInput>(
  exchange: ExchangeRunnerState<TInput> | null
): exchange is ExchangeRunnerState<TInput> => exchange !== null && IN_FLIGHT_STATUSES.includes(exchange.status);

export const useExchangeRunner = <TInput, TExtra, TExchange, TRow extends ExchangeRow, TToken>({
  transport,
  rows,
  isConnected,
  resetKey,
  onResolved,
  onRealTimeToken,
}: UseExchangeRunnerParams<TInput, TExtra, TExchange, TRow, TToken>): UseExchangeRunnerReturn<TInput, TExtra> => {
  const [active, setActive] = React.useState<ExchangeRunnerState<TInput> | null>(null);
  const [isFetching, setIsFetching] = React.useState(false);

  // Read latest state/props inside the poll timeout + event callbacks without stale closures.
  const activeRef = React.useRef<ExchangeRunnerState<TInput> | null>(null);
  // Which exchange has a GET in flight — keyed per exchange so a slow/hung request for a previous
  // exchange can't block the fetch/poll of a newly submitted or retried one.
  const inFlightExchangeIdRef = React.useRef<string | null>(null);
  const onResolvedRef = React.useRef(onResolved);
  const onRealTimeTokenRef = React.useRef(onRealTimeToken);
  // Recovery reads the row predicates through this, so it re-runs on what the chat loaded and on the
  // run's state only: a transport rebuilt for a new route does not change what a row means.
  const transportRef = React.useRef(transport);

  React.useEffect(() => {
    activeRef.current = active;
  }, [active]);
  React.useEffect(() => {
    onResolvedRef.current = onResolved;
    onRealTimeTokenRef.current = onRealTimeToken;
    transportRef.current = transport;
  }, [onResolved, onRealTimeToken, transport]);

  // Switching conversations must not leak the previous chat's exchange into the new one — otherwise
  // the poll reads a foreign exchange id under the new chat and the loader stays stuck. Tear the local
  // state down; the reload-recovery effect below re-attaches if the freshly loaded chat has an
  // exchange still running.
  React.useEffect(() => {
    setActive(null);
    inFlightExchangeIdRef.current = null;
  }, [resetKey]);

  // `retriesLeft`: the `done` event fires once per exchange, so its fetch is retried on a transient
  // failure rather than waiting up to a minute for the connected poll. The poll passes none — its own
  // interval is already the retry.
  const fetchExchange = React.useCallback(
    async (exchangeId: string, retriesLeft = 0) => {
      if (inFlightExchangeIdRef.current === exchangeId) return;
      inFlightExchangeIdRef.current = exchangeId;
      try {
        const result = await transport.fetch(exchangeId);

        // A newer submit/retry replaced this exchange while the request was in flight — drop the result.
        if (activeRef.current?.exchangeId !== exchangeId) return;

        if (result.status === ASYNC_EXCHANGE_STATUS.COMPLETED) {
          onResolvedRef.current(result.exchange);
          setActive(null);
        } else if (result.status === ASYNC_EXCHANGE_STATUS.FAILED) {
          setActive((prev) =>
            prev?.exchangeId === exchangeId
              ? { ...prev, status: ASYNC_EXCHANGE_STATUS.FAILED, error: result.error }
              : prev
          );
        }
      } catch (error) {
        // The global axiosRetry (CmsMain) never retries a 429, which is what the edge rate limiter returns.
        // `isNil`: axios 0.21's isAxiosError throws on null.
        const transient = !isNil(error) && axios.isAxiosError(error) && isTransientStatus(error.response?.status);
        if (transient && retriesLeft > 0) {
          // Skip the retry if a newer submit/retry has replaced this exchange in the meantime.
          setTimeout(() => {
            if (activeRef.current?.exchangeId === exchangeId) fetchExchange(exchangeId, retriesLeft - 1);
          }, ASYNC_POLL_INTERVAL_MS);
          return;
        }
        // Retries spent, or not transient. A transient failure is reported without the global dialog,
        // which would block the rest of the app while the poll keeps trying; a 401/403/404 keeps it.
        // `.catch`: the report's own POST can fail in the same outage, and the rejection would escape to
        // the poll and stop it.
        await reportAxiosError(error, !transient).catch(() => null);
      } finally {
        if (inFlightExchangeIdRef.current === exchangeId) {
          inFlightExchangeIdRef.current = null;
        }
      }
    },
    [transport]
  );

  const submit = React.useCallback(
    async (input: TInput, extra?: TExtra) => {
      // Show the loader immediately — before the request lands and flips `active` to processing (the
      // sync effect below keeps it in step from there).
      setIsFetching(true);
      try {
        const { exchangeId, realTimeToken } = await transport.submit(input, extra);

        setActive({ exchangeId, input, status: ASYNC_EXCHANGE_STATUS.PROCESSING });

        if (!isNil(realTimeToken)) onRealTimeTokenRef.current(realTimeToken);
      } catch (error) {
        setIsFetching(false);
        await reportAxiosError(error);
        throw error;
      }
    },
    [transport]
  );

  // `extra` is not repeated: it shaped that one send, and a retry re-sends the same input as a new
  // exchange (a failed regenerate is retried as a plain submit).
  const retry = React.useCallback(async () => {
    const failed = activeRef.current;
    if (!failed) return;
    await submit(failed.input);
  }, [submit]);

  const refresh = React.useCallback(
    (exchangeId: string) => fetchExchange(exchangeId, ASYNC_FETCH_RETRIES),
    [fetchExchange]
  );

  // Loader in step with the machine: on while processing, off once completed (null) or failed.
  React.useEffect(() => {
    setIsFetching(isExchangeInFlight(active));
  }, [active]);

  // Fallback poll while processing (Firebase-down, no token, dropped `done`, page reload). Self-
  // rescheduling timeout, not setInterval, so a slow fetch can't stack overlapping polls. Slow cadence
  // while the subscription is connected (the `done` event carries the happy path), faster when it's
  // down. maxAttempts derives from the interval so the ~15 min stuck-run → `failed` ceiling holds either way.
  React.useEffect(() => {
    if (!isExchangeInFlight(active)) return;
    const { exchangeId } = active;
    const pollIntervalMs = isConnected ? ASYNC_POLL_INTERVAL_CONNECTED_MS : ASYNC_POLL_INTERVAL_MS;
    const maxAttempts = Math.ceil(ASYNC_POLL_TIMEOUT_MS / pollIntervalMs);
    let attempts = 0;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const scheduleNext = () => {
      timer = setTimeout(async () => {
        attempts += 1;
        if (attempts > maxAttempts) {
          setActive((prev) =>
            prev?.exchangeId === exchangeId ? { ...prev, status: ASYNC_EXCHANGE_STATUS.FAILED, error: null } : prev
          );
          return;
        }
        await fetchExchange(exchangeId);
        // The effect tore down (resolved / chat switch / unmount) while the fetch was in flight — stop.
        if (!cancelled) scheduleNext();
      }, pollIntervalMs);
    };

    scheduleNext();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [active, isConnected, fetchExchange]);

  // Reload / conversation-switch recovery: a persisted exchange with no answer is still running, so
  // re-attach (with its input, for a later retry) and let the subscription + poll resolve it.
  React.useEffect(() => {
    if (active || isFetching) return;
    const last = rows[rows.length - 1];
    const { isInFlight, inputOf } = transportRef.current;
    if (last && isInFlight(last)) {
      setActive({ exchangeId: String(last.id), input: inputOf(last), status: ASYNC_EXCHANGE_STATUS.PROCESSING });
    }
  }, [rows, active, isFetching]);

  return { active, isFetching, submit, retry, refresh };
};
